import { createContext, useReducer, useContext, useEffect } from "react";
import { productsContent } from "../constants"; // Assuming this is your product data source

// Define action types
const ADD_TO_CART = "ADD_TO_CART";
const REMOVE_FROM_CART = "REMOVE_FROM_CART";
const REMOVE_COMPLETELY = "REMOVE_COMPLETELY";
const CLEAR_CART = "CLEAR_CART";
const LOAD_CART = "LOAD_CART";
const REMOVE_OUT_OF_STOCK = "REMOVE_OUT_OF_STOCK";
const SYNC_CART_WITH_PRODUCTS = "SYNC_CART_WITH_PRODUCTS"; // New action type to sync cart with product data

// Helper function to get cart from localStorage
const getCartFromStorage = () => {
  if (typeof window !== 'undefined') {
    const storedCart = localStorage.getItem('cart');
    return storedCart ? JSON.parse(storedCart) : [];
  }
  return [];
};

// Cart reducer function to manage cart actions
const cartReducer = (state, action) => {
  switch (action.type) {
    case ADD_TO_CART:
      const existingItem = state.cart.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          cart: state.cart.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
        };
      }
      return { ...state, cart: [...state.cart, { ...action.payload, quantity: 1 }] };

    case REMOVE_FROM_CART:
      return {
        ...state,
        cart: state.cart
          .map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity - 1 }
              : item
          )
          .filter(item => item.quantity > 0),
      };

    case REMOVE_COMPLETELY:
      return {
        ...state,
        cart: state.cart.filter(item => item.id !== action.payload.id),
      };

    case CLEAR_CART:
      return { ...state, cart: [] };

    case LOAD_CART:
      return { ...state, cart: action.payload };

    case REMOVE_OUT_OF_STOCK:
      const updatedCart = state.cart.filter(item => item.inStock);
      const removedItems = state.cart.filter(item => !item.inStock);
      return {
        ...state,
        cart: updatedCart,
        removedItems,
      };

    case SYNC_CART_WITH_PRODUCTS:
      // Sync cart items with the latest product data
      const syncedCart = state.cart.map(cartItem => {
        const product = productsContent.find(p => p.id === cartItem.id);
        return product ? { ...cartItem, inStock: product.inStock } : cartItem;
      });
      return {
        ...state,
        cart: syncedCart,
      };

    default:
      return state;
  }
};

// Create Context
const CartContext = createContext();

// CartProvider component to manage cart state and provide it to the app
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { cart: [], removedItems: [] }, () => {
    return { cart: getCartFromStorage(), removedItems: [] };
  });

  // Sync cart state with localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cart', JSON.stringify(state.cart));
    }
  }, [state.cart]);

  // Sync cart with product data whenever productsContent changes
  useEffect(() => {
    dispatch({ type: SYNC_CART_WITH_PRODUCTS });
  }, [productsContent]);

  // Check for out-of-stock items periodically
  useEffect(() => {
    const checkOutOfStockItems = () => {
      const outOfStockItems = state.cart.filter(item => !item.inStock);
      if (outOfStockItems.length > 0) {
        dispatch({ type: REMOVE_OUT_OF_STOCK });
        // Show alert for removed items
        outOfStockItems.forEach(item => {
          alert(`${item.name} is out of stock and has been removed from your cart.`);
        });
      }
    };

    // Check for out-of-stock items every 5 seconds (or any interval you prefer)
    const interval = setInterval(checkOutOfStockItems, 5000);
    return () => clearInterval(interval); // Cleanup interval on unmount
  }, [state.cart]);

  // Action creators
  const addToCart = (item) => {
    dispatch({ type: ADD_TO_CART, payload: item });
  };

  const removeFromCart = (item) => dispatch({ type: REMOVE_FROM_CART, payload: item });
  const removeCompletely = (item) => dispatch({ type: REMOVE_COMPLETELY, payload: item });
  const clearCart = () => dispatch({ type: CLEAR_CART });

  // Calculate total items
  const getTotalItems = () => state.cart.reduce((total, item) => total + item.quantity, 0);

  // Calculate total price
  const getTotalPrice = () => state.cart.reduce(
    (total, item) => total + (item.price * item.quantity), 0
  );

  // Provide cart state and actions to the app
  return (
    <CartContext.Provider value={{
      cart: state.cart,
      addToCart,
      removeFromCart,
      removeCompletely,
      clearCart,
      getTotalItems,
      getTotalPrice,
      removedItems: state.removedItems,
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Custom Hook for using Cart Context
export const useCart = () => useContext(CartContext);